import React, { useEffect, useId, useRef, useState } from 'react'
import { INITIAL_CONTEXT_MENU_STATE, PROJECT, ProjectRoles, projectRoleLabel } from 'app-constants'
import { IContextMenuState, IMemberInvite, ITableWithCells } from 'types'
import api from 'helpers/api'
import { isValidEmail } from 'helpers/utils'
import useProject from 'hooks/useProject'
import { useHubFailure, useShownHub } from 'hooks/useShownHub'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Field from 'components/field'
import FilterMenu, { FIELD_MENU_BUTTON } from 'components/filter-menu'
import HoverCard from 'components/hover-card'
import Menu from 'components/menu'
import { MenuItem, MenuList } from 'components/menu/items'
import { popoverPlacement } from 'components/popover'
import { BLUE_BUTTON } from 'components/save-buttons'
import { ChevronDown, Tick } from 'components/icons'
import Badge from 'components/badge'
import {
  needsSeat,
  PAID_SEAT_REQUIRED,
  ROLE_DESCRIPTIONS,
  ROLES,
  tagGroups
} from 'views/settings/components/permissions/utils'
import EmailsField, { addressesIn } from './emailsField'

const LABEL = 'text-[13px] font-medium text-body'
const FIELD = 'flex flex-col gap-1.5'
const HINT = 'text-xs leading-4 text-muted'

/**
 * The role to invite with: a field's button that opens our menu of the roles, each saying what it allows, and
 * Owner and Builder badged as needing a paid seat.
 */
const RoleField: React.FC<{ role: string; onChange: (role: ProjectRoles) => void; labelledBy: string }> = ({
  role,
  onChange,
  labelledBy
}) => {
  const [menu, setMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [width, setWidth] = useState(0)
  return (
    <>
      <Button
        internalType="outline"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        // "Role Member": the field's label, then what's chosen.
        aria-labelledby={`${labelledBy} ${labelledBy}-value`}
        className={FIELD_MENU_BUTTON}
        // The menu closes on a press outside it, and this button counts as outside: while it's open, keep the
        // press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (menu.open) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (menu.open) return setMenu(INITIAL_CONTEXT_MENU_STATE)
          const rect = event.currentTarget.getBoundingClientRect()
          setWidth(rect.width)
          setMenu(popoverPlacement(rect, { align: 'start', width: rect.width, height: ROLES.length * 52 + 8 }))
        }}
      >
        <span id={`${labelledBy}-value`} className="truncate leading-5">
          {projectRoleLabel(role)}
        </span>
        <ChevronDown />
      </Button>
      {menu.open && (
        <Menu id="invite-role" menuState={menu} setMenuState={setMenu} width={width} fixed>
          <MenuList>
            {ROLES.map((each) => (
              <MenuItem
                key={each}
                description={ROLE_DESCRIPTIONS[each]}
                end={each === role ? <Tick /> : undefined}
                onClick={() => {
                  setMenu(INITIAL_CONTEXT_MENU_STATE)
                  onChange(each)
                }}
              >
                <span className="flex items-center gap-2">
                  {projectRoleLabel(each)}
                  {needsSeat(each) && <Badge>{PAID_SEAT_REQUIRED}</Badge>}
                </span>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
    </>
  )
}

const TAGS_EXPLAINED = `Documents, tables and views can be shared with a tag, giving access to every member who has it. A ${PROJECT}'s tags come from its tag tables: tables with a Tag column.`

/** The tags chosen from one tag table: a field's button listing them, opening a menu to tick them. */
const TagsField: React.FC<{ tagTable: ITableWithCells; selected: string[]; onChange: (tags: string[]) => void }> = ({
  tagTable,
  selected,
  onChange
}) => {
  const names = tagTable.cells
    .filter((cell) => selected.includes(cell.id))
    .map((cell) => cell.value)
    .sort()
    .join(', ')
  return (
    <div className={FIELD}>
      <span className={LABEL}>
        <HoverCard title="Tags" content={TAGS_EXPLAINED}>
          Tag
        </HoverCard>
        <span className="text-muted"> – </span>
        {tagTable.name}
      </span>
      <FilterMenu
        noun={{ one: 'tag', many: 'tags' }}
        groups={tagGroups(tagTable)}
        selected={selected}
        onChange={(tags) => onChange(tags ?? [])}
        pick
        label={`${tagTable.name} tags`}
        buttonLabel={names || <span className="text-faint">Add tags</span>}
        labelInFull={names}
        buttonClassName={FIELD_MENU_BUTTON}
        matchWidth
      />
    </div>
  )
}

/** The ids of a tag table's tags that are among those given. */
const tagsIn = (tagTable: ITableWithCells, tags: string[]) =>
  tagTable.cells.filter((cell) => tags.includes(cell.id)).map((cell) => cell.id)

interface InviteDialogProps {
  /** An invite to edit: its role and tags. Without one, the dialog invites people. */
  invite?: IMemberInvite
  onClose: () => void
  /** The invites were sent, or the invite saved. */
  onDone: () => void
}

/**
 * Invites people to the hub, or edits an invite not yet taken up, in our dialog: the email addresses as chips
 * (not when editing), the role they'll have, and their tags from each of the hub's tag tables.
 */
const InviteDialog: React.FC<InviteDialogProps> = ({ invite, onClose, onDone }) => {
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const stillShown = useShownHub(project.publicId)
  const hubFailed = useHubFailure(project.publicId)
  const id = useId()
  const [emails, setEmails] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [role, setRole] = useState<string>(invite ? invite.projectRole : ProjectRoles.MEMBER)
  const [tags, setTags] = useState<string[]>(invite ? invite.tags.map((tag) => tag.referencePublicId) : [])
  const [tagTables, setTagTables] = useState<ITableWithCells[]>([])
  const [sending, setSending] = useState(false)
  const [checked, setChecked] = useState(false)

  // The hub it was opened in. Another hub's role and tags would mean nothing here, so switching hubs (the
  // sidebar's invite stays open across a switch) closes it.
  const openedIn = useRef(project.publicId)
  useEffect(() => {
    if (project.publicId !== openedIn.current) onClose()
  }, [project.publicId])

  useEffect(() => {
    // A response for a hub no longer shown is dropped, success or failure.
    let cancelled = false
    api
      .getProjectTags(project.publicId)
      .then((response) => {
        if (!cancelled) setTagTables(response.data)
      })
      .catch((error) => {
        if (!cancelled) displayErrorMessage(error)
      })
    return () => {
      cancelled = true
    }
  }, [project.publicId])

  // Everything typed counts, including an address not yet made a chip.
  const all = [...emails, ...addressesIn(draft, emails)]
  const invalid = all.filter((email) => !isValidEmail(email))
  const showInvalid = checked && invalid.length > 0

  const send = async () => {
    setEmails(all)
    setDraft('')
    setChecked(true)
    if (!invite && invalid.length > 0) return
    setSending(true)
    const hub = project.publicId
    const hubName = project.name
    try {
      if (invite) await api.updateInvite(invite, { projectId: hub, tags, projectRole: role })
      else await api.createInvite({ projectId: hub, emails: all, tags, projectRole: role })
    } catch (error) {
      setSending(false)
      hubFailed(
        error,
        hub,
        invite
          ? `The invite to ${invite.email} in ${hubName} couldn't be updated. Open ${hubName}'s settings to try again.`
          : `Invitations to ${hubName} couldn't be sent. Open ${hubName}'s settings to try again.`
      )
      return
    }
    setSending(false)
    // Finished once another hub is shown: that hub's page isn't told, and the message names the hub it was for.
    if (stillShown(hub)) onDone()
    else
      setSnackbarMessage({
        status: 'success',
        message: invite
          ? `The invite to ${invite.email} in ${hubName} was updated`
          : `Invitations to ${hubName} were sent`
      })
  }

  const invalidMessage =
    invalid.length === 1
      ? `${invalid[0]} isn't a valid email address. Correct or remove it to send.`
      : `${invalid.length} addresses aren't valid. Correct or remove them to send.`

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={invite ? 'Edit Invite' : 'Invite Members'}
      description={
        invite
          ? `${invite.email} joins this ${PROJECT} with this role and these tags when they sign up.`
          : `People with a Morta account join this ${PROJECT} straight away. Anyone else is emailed an invite, and is listed as invited until they sign up.`
      }
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            isLoading={sending}
            disabled={!invite && all.length === 0}
            onClick={send}
          >
            {invite ? 'Save' : 'Send Invites'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {!invite && (
          <Field label="Email addresses" htmlFor={`${id}-emails`} className={FIELD} labelClassName={LABEL}>
            <EmailsField
              id={`${id}-emails`}
              emails={emails}
              onChange={setEmails}
              draft={draft}
              onDraftChange={setDraft}
              invalid={showInvalid}
              describedBy={`${id}-emails-hint`}
            />
            <p id={`${id}-emails-hint`} className={showInvalid ? `${HINT} text-red` : HINT}>
              {showInvalid ? invalidMessage : 'Press Enter or a comma after each address, or paste a list.'}
            </p>
          </Field>
        )}

        <div className={FIELD}>
          <span id={`${id}-role`} className={LABEL}>
            Role
          </span>
          <RoleField role={role} onChange={setRole} labelledBy={`${id}-role`} />
        </div>

        {tagTables.map((tagTable) => (
          <TagsField
            key={tagTable.publicId}
            tagTable={tagTable}
            selected={tagsIn(tagTable, tags)}
            onChange={(chosen) => setTags([...tags.filter((tag) => !tagsIn(tagTable, [tag]).length), ...chosen])}
          />
        ))}
      </div>
    </Dialog>
  )
}

export default InviteDialog
